import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { AppBackground } from "@/components/app-background";
import { queryClient } from "@/lib/query-client";
import { appState } from "@/lib/app-state";
import { cloneSession, isValidSession, queryKeys } from "@listto/shared";
import { PageTransition } from "@/components/ui/page-transition";

function AuthLayout() {
  const location = useLocation();
  appState.markAsReady();
  return (
    <AppBackground class="flex min-h-dvh items-center justify-center p-4">
      <div class="w-full max-w-sm">
        <PageTransition routeKey={location().pathname}>
          <Outlet />
        </PageTransition>
      </div>
    </AppBackground>
  );
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    // The redirect is thrown AFTER the try/catch so TanStack Router receives it.
    // No initial value: TypeScript narrows `result` after the catch-that-returns.
    // oxlint-disable-next-line eslint(init-declarations)
    let result;
    try {
      result = await api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
    } catch {
      // Network error — let user try to log in anyway.
      return;
    }

    const { data, error } = result;

    // Synchronize the machine and the cache immediately.
    // This prevents the BootManager from seeing a "pending" or stale session
    // during the route transition, which causes redirection loops.
    sessionActor.send({
      type: "SERVER_REPLY",
      session: isValidSession(data) ? cloneSession(data) : null,
      error: error ?? null,
    });

    queryClient.setQueryData(queryKeys.auth.session(), data ?? null);

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Better Auth returns HTTP 200 with a null body for unauthenticated requests,
    // but the OpenAPI schema types Session as non-null. Cast to match runtime reality.
    const sessionData = data as { user?: unknown } | null | undefined;
    if (sessionData?.user) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayout,
});
